// ====================== TeamSlider DEBUG ======================
const TeamDBG = (() => {
  const on = !!(window.__TEAM_DEBUG__ ?? false);
  const tag = (id) => `%c[TeamSlider#${id}]`;
  const style = 'color:#7B5CFF;font-weight:600';

  function log(...args) { if (on) console.log(tag('log'), style, ...args); }

  return {
    on,
    markInited(root, id)       { if (on) log('inited', id, root); },
    logInit(root, id, slides, thumbs, interval) { if (on) log('init', { id, slides: slides.length, thumbs: thumbs.length, interval }); },
    logSetActive(root, id, prev, next) { if (on) log('setActive', { id, prev, next }); },
    logShow(root, id, idx, byUser)     { if (on) log('show', { id, idx, byUser }); },
    logClick(root, id, idx)            { if (on) log('click', { id, idx }); },
    logIO(root, id, v)                 { if (on) log('IO', { id, intersecting: v }); },
    logVisibility(root, id, st)        { if (on) log('visibility', { id, st }); },
    hookRing(r, id)                    { /* no-op */ }
  };
})();
window.TeamDBG = TeamDBG;
// =================== /TeamSlider DEBUG ========================


// ====================== TeamSlider CORE =======================
export default function initTeamSliders() {
  document.querySelectorAll('.team-slider:not(.js-inited)').forEach(root => {
    root.classList.add('js-inited');
    initTeamSlider(root);
  });
}

function initTeamSlider(root) {
  const slides = Array.from(root.querySelectorAll('.team-slide'));
  const thumbs = Array.from(root.querySelectorAll('.team-thumb'));
  if (slides.length <= 1) return;

  const id = root.closest('[data-section]')?.getAttribute('data-section')
      || root.getAttribute('data-section')
      || Math.random().toString(36).slice(2,7);

  // — устойчивый парсер ms/s/число
  const readMs = (v, def) => {
    if (v == null || v === '') return def;
    const s = String(v).trim();
    if (/ms$/i.test(s)) return Math.max(0, parseFloat(s));
    if (/s$/i.test(s))  return Math.max(0, parseFloat(s) * 1000);
    const n = Number(s);
    return Number.isFinite(n) ? Math.max(0, n) : def;
  };

  const autoplayAttr = root.dataset.autoplay === '1';
  const interval     = Math.max(1000, readMs(root.dataset.interval,   6000));
  const transition   = Math.max( 100, readMs(root.dataset.transition,  600));
  root.style.setProperty('--interval', `${interval}ms`);
  root.style.setProperty('--t', `${transition}ms`);

  const forceAnim = /[?&]anim=1\b/.test(location.search) || document.documentElement.dataset.ignorePrm === '1';
  const prefersReduced = forceAnim
      ? false
      : !!(window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches);

  TeamDBG.markInited(root, id);
  TeamDBG.logInit(root, id, slides, thumbs, interval);

  let i = slides.findIndex(s => s.classList.contains('is-active'));
  if (i < 0) i = 0;

  slides.forEach((s, idx) => s.classList.toggle('is-active', idx === i));
  thumbs.forEach((t, idx) => {
    t.classList.toggle('is-active', idx === i);
    t.setAttribute('aria-pressed', idx === i ? 'true' : 'false');
  });

  // — автоплей и пользовательское состояние
  let autoplayEnabled = false; // включает/выключает логика видимости
  let userStopped = false;     // после клика по миниатюре — ручной режим

  // — состояние кольца
  let currentRing = null;
  let currentRunId = 0;
  let ringState = 'stopped'; // 'running' | 'paused' | 'stopped'

  const section = root.closest('.team-section');

  const applyAria = () => {
    slides.forEach((s, idx) => s.setAttribute('aria-hidden', idx === i ? 'false' : 'true'));
  };
  applyAria();

  const getActiveRing = () => thumbs[i]?.querySelector('.ring-progress');

  const detachRingHandlers = () => {
    if (!currentRing) return;
    currentRing.onanimationend = null;
    currentRing.ontransitionend = null;
    currentRing = null;
  };

  const hardStopAllRings = () => {
    detachRingHandlers();
    root.querySelectorAll('.ring-progress').forEach(r => {
      r.style.animation = 'none';
      r.style.animationPlayState = 'paused';
      r.style.strokeDashoffset = '100';
    });
    ringState = 'stopped';
  };

  const pauseActiveRing = () => {
    if (currentRing && ringState === 'running') {
      currentRing.style.animationPlayState = 'paused';
      ringState = 'paused';
    }
  };

  const resumeActiveRing = () => {
    if (userStopped || !autoplayEnabled || prefersReduced) return;
    if (currentRing) {
      if (ringState !== 'running') {
        currentRing.style.animationPlayState = 'running';
        ringState = 'running';
      }
    } else {
      // первый запуск / после hard stop
      const ring = getActiveRing();
      if (ring) {
        restartRing(ring, interval);
        ringState = 'running';
      }
    }
  };

  const restartRing = (ring, ms) => {
    if (!ring) return;
    detachRingHandlers();
    currentRunId++;

    // сброс состояния
    ring.style.animation = 'none';
    ring.style.animationPlayState = 'paused';
    ring.style.strokeDashoffset = '100';
    ring.getBoundingClientRect(); // reflow

    const runId = currentRunId;
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        ring.style.animation = `ring-progress ${ms}ms linear forwards`;
        ring.style.animationPlayState = 'running';
        currentRing = ring;
        ringState = 'running';

        const done = () => {
          if (runId !== currentRunId) return;
          currentRing = null;
          ringState = 'stopped';
          if (autoplayEnabled && !userStopped) next();
        };
        ring.onanimationend = done;
        ring.ontransitionend = done;
      });
    });
  };

  // старый API — теперь работает через pause/resume
  const setThumbProgress = (running) => {
    if (userStopped) return;
    if (!running) {
      pauseActiveRing();
      return;
    }
    if (currentRing && ringState === 'running') return; // уже идёт — ничего не трогаем
    resumeActiveRing(); // либо продолжить, либо первый старт
  };

  const setActive = (idx) => {
    TeamDBG.logSetActive(root, id, i, idx);
    hardStopAllRings();

    slides[i].classList.remove('is-active');
    thumbs[i]?.classList.remove('is-active');
    thumbs[i]?.setAttribute('aria-pressed', 'false');

    i = (idx + slides.length) % slides.length;

    slides[i].classList.add('is-active');
    thumbs[i]?.classList.add('is-active');
    thumbs[i]?.setAttribute('aria-pressed', 'true');
    applyAria();
  };

  const next = () => show(i + 1);

  const show = (idx, byUser = false) => {
    TeamDBG.logShow(root, id, idx, byUser);
    if (idx === i) return;

    setActive(idx);

    // визуальное кольцо только если это авто/системный переход и пользователь не выключил
    if (!byUser && autoplayEnabled && !userStopped && !prefersReduced) {
      resumeActiveRing(); // не рестартить впустую
    }
  };

  // === КЛИК ПО МИНИАТЮРЕ: выключаем автоплей «навсегда» (до перезагрузки)
  thumbs.forEach((btn, idx) => {
    btn.addEventListener('click', () => {
      TeamDBG.logClick(root, id, idx);
      hardStopAllRings();
      show(idx, true);        // byUser=true
      autoplayEnabled = false;
      userStopped = true;     // фиксируем ручной режим
    });
  });

  // hover-пауза (мягкая)
  root.addEventListener('mouseenter', () => {
    if (!userStopped && autoplayEnabled) pauseActiveRing();
  });
  root.addEventListener('mouseleave', () => {
    if (!userStopped && autoplayEnabled && !prefersReduced) {
      resumeActiveRing();
    }
  });

  // стартовое состояние
  hardStopAllRings();

  // автоплей по видимости (НЕ включаем, если userStopped станет true позже)
  const kickIfVisibleNow = () => {
    const host = section || root;
    if (!host) return;
    const r  = host.getBoundingClientRect();
    const vh = window.innerHeight || document.documentElement.clientHeight;
    const visible = r.top < vh * 0.95 && r.bottom > vh * 0.05;
    if (visible && autoplayAttr && !userStopped && !prefersReduced) {
      autoplayEnabled = true;
      resumeActiveRing();
    }
  };

  if (section && autoplayAttr && !prefersReduced) {
    const io = new IntersectionObserver(entries => {
      for (const entry of entries) {
        TeamDBG.logIO(root, id, entry.isIntersecting);
        if (entry.isIntersecting && !userStopped) {
          autoplayEnabled = true;
          resumeActiveRing();
        } else {
          autoplayEnabled = false;
          hardStopAllRings();
        }
      }
    }, { threshold: 0, rootMargin: '-80px 0px -5% 0px' });

    io.observe(section);
    queueMicrotask(kickIfVisibleNow);
  }

  // вкладка/история/восстановление из bfcache
  const onVisibility = () => {
    TeamDBG.logVisibility(root, id, document.visibilityState);
    if (document.visibilityState === 'visible' && autoplayAttr && !userStopped && !prefersReduced) {
      autoplayEnabled = true;
      resumeActiveRing();
    } else {
      autoplayEnabled = false;
      hardStopAllRings();
    }
  };
  document.addEventListener('visibilitychange', onVisibility);

  window.addEventListener('pageshow', (e) => {
    if (e.persisted && autoplayAttr && !userStopped && !prefersReduced) {
      autoplayEnabled = true;
      resumeActiveRing();
    }
  });

  // «тишина» скролла → только resume, без рестартов
  const onScrollSettled = () => {
    if (autoplayAttr && !userStopped && !prefersReduced) {
      const host = section || root;
      if (!host) return;
      const r  = host.getBoundingClientRect();
      const vh = window.innerHeight || document.documentElement.clientHeight;
      const visible = r.top < vh * 0.95 && r.bottom > vh * 0.05;
      if (visible && autoplayEnabled) resumeActiveRing();
    }
  };
  window.addEventListener('scrollend', onScrollSettled, { passive: true });
  let __scrollTO;
  window.addEventListener('scroll', () => {
    clearTimeout(__scrollTO);
    __scrollTO = setTimeout(onScrollSettled, 200); // увеличили паузу «тишины»
  }, { passive: true });
  window.addEventListener('hab:scroll-done', onScrollSettled, { passive: true });

  // дебаг-хуки на кольца
  root.querySelectorAll('.ring-progress').forEach(r => TeamDBG.hookRing(r, id));
}
// ==================== /TeamSlider CORE =======================
